import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Badge,
  V2Card,
  V2EmptyState,
} from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import type {
  Customer360Response,
  CustomerCommunicationChannel,
  CustomerListResponse,
} from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { CustomerCommunicationsPanel } from './CustomerCommunicationsPanel.js';
import './communicationWorkspace.css';

const CHANNEL_COPY = {
  sms: {
    searchPlaceholder: 'جست‌وجوی مشتری برای مشاهده پیامک‌ها',
    emptyTitle: 'یک مشتری را انتخاب کنید',
    emptyDescription:
      'پس از انتخاب مشتری، وضعیت رضایت و تاریخچه واقعی پیامک‌های او نمایش داده می‌شود.',
    contactLabel: 'شماره پیامک',
  },
  voice: {
    searchPlaceholder: 'جست‌وجوی مشتری برای مشاهده تماس‌ها',
    emptyTitle: 'یک مشتری را انتخاب کنید',
    emptyDescription:
      'پس از انتخاب مشتری، وضعیت رضایت و تاریخچه واقعی تماس‌های او نمایش داده می‌شود.',
    contactLabel: 'شماره تماس',
  },
} as const;

/** Speech-bubble mark for the SMS channel (the design-system set has no message icon; `bell` read as notifications). */
function SmsMark({ size = 26 }: { readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 5.5h16v11h-9.5L6 20v-3.5H4z" />
      <path d="M8 10h8M8 13h5" />
    </svg>
  );
}

export function CommunicationWorkspacePage(props: {
  readonly channel: CustomerCommunicationChannel;
  readonly title: string;
  readonly subtitle: string;
  readonly icon: 'sms' | 'phone';
  /** Inside another page (e.g. a tab): drop the page header. */
  readonly embedded?: boolean;
}) {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const selectedRef = params.get('customer') ?? '';
  const listParams = new URLSearchParams({ limit: '20', offset: '0', sort: 'name', order: 'asc' });
  if (query.trim()) listParams.set('search', query.trim());

  const customers = useApiGet<CustomerListResponse>(
    slug ? `${base}/api/v1/crm/customers?${listParams.toString()}` : null,
    [slug, query],
  );
  const selected = useApiGet<Customer360Response>(
    slug && selectedRef ? `${base}/api/v1/crm/customers/${encodeURIComponent(selectedRef)}` : null,
    [slug, selectedRef],
  );

  if (!slug) return null;

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  }

  const customer = selected.status === 'ready' ? selected.data.customer : null;
  const endpoint =
    props.channel === 'sms'
      ? (customer?.mobile ?? null)
      : (customer?.phone ?? customer?.mobile ?? null);
  const href = endpoint ? `${props.channel === 'sms' ? 'sms' : 'tel'}:${endpoint}` : null;
  const copy = CHANNEL_COPY[props.channel];

  return (
    <div className="communication-workspace">
      {props.embedded ? null : (
        <header className="communication-workspace__head">
          <span className="communication-workspace__icon">
            {props.icon === 'sms' ? <SmsMark /> : <Icon name={props.icon} size="lg" />}
          </span>
          <div>
            <h1>{props.title}</h1>
            <p>{props.subtitle}</p>
          </div>
        </header>
      )}

      <div className="communication-workspace__layout">
        <V2Card>
          <section className="communication-picker" aria-label="انتخاب مشتری">
            <div>
              <h2>انتخاب مشتری</h2>
              <p>نام یا کد مشتری را جست‌وجو کنید.</p>
            </div>
            <input
              className="sk-input"
              type="search"
              value={query}
              placeholder={copy.searchPlaceholder}
              aria-label={copy.searchPlaceholder}
              onChange={(event) => patchParams({ q: event.target.value || null })}
            />

            {customers.status === 'loading' ? <LoadingState /> : null}
            {customers.status === 'error' ? (
              <StatePanel
                variant={customers.error.status === 403 ? 'permission_denied' : 'network_error'}
                title={
                  customers.error.status === 403
                    ? 'برای مشاهده مشتریان دسترسی ندارید'
                    : 'فهرست مشتریان در دسترس نیست'
                }
              />
            ) : null}
            {customers.status === 'ready' ? (
              customers.data.items.length > 0 ? (
                <div className="communication-picker__list">
                  {customers.data.items.map((item) => {
                    const ref = item.ref ?? '';
                    const active = ref === selectedRef;
                    const contact =
                      props.channel === 'sms' ? item.mobile : (item.phone ?? item.mobile);
                    return (
                      <button
                        key={ref || item.name}
                        type="button"
                        className="communication-picker__item"
                        data-active={active ? 'true' : undefined}
                        disabled={!ref}
                        onClick={() => patchParams({ customer: ref || null })}
                      >
                        <span>
                          <strong>{item.name ?? 'بدون نام'}</strong>
                          <small className="c360-ltr">{ref || 'بدون کد'}</small>
                        </span>
                        <span className="communication-picker__contact c360-ltr">
                          {contact ?? 'شماره ثبت نشده'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <V2EmptyState
                  icon="users"
                  title="مشتری‌ای پیدا نشد"
                  description="عبارت جست‌وجو را تغییر دهید."
                  compact
                />
              )
            ) : null}
          </section>
        </V2Card>

        <V2Card>
          <section className="communication-customer" aria-label="مشتری انتخاب‌شده">
            {!selectedRef ? (
              <V2EmptyState
                icon={props.icon === 'sms' ? 'search' : props.icon}
                title={copy.emptyTitle}
                description={copy.emptyDescription}
                compact
              />
            ) : selected.status === 'loading' ? (
              <LoadingState />
            ) : selected.status === 'error' ? (
              <StatePanel
                variant={selected.error.status === 403 ? 'permission_denied' : 'network_error'}
                title="پرونده مشتری قابل دریافت نیست"
              />
            ) : customer ? (
              <>
                <div className="communication-customer__head">
                  <div>
                    <div className="communication-customer__title">
                      <h2>{customer.name ?? customer.ref}</h2>
                      <V2Badge tone={customer.status === 'active' ? 'success' : 'neutral'}>
                        {customer.status === 'active' ? 'فعال' : 'غیرفعال'}
                      </V2Badge>
                    </div>
                    <span className="c360-ltr communication-customer__code">{customer.ref}</span>
                  </div>
                </div>
                <dl className="communication-customer__facts">
                  <div>
                    <dt>{copy.contactLabel}</dt>
                    <dd className="c360-ltr">{endpoint ?? 'ثبت نشده'}</dd>
                  </div>
                  <div>
                    <dt>ایمیل</dt>
                    <dd className="c360-ltr">{customer.email ?? 'ثبت نشده'}</dd>
                  </div>
                </dl>
                <div className="communication-customer__actions">
                  <Link
                    className="sk-btn sk-btn--secondary sk-btn--sm"
                    to={`${base}/crm/customers/${encodeURIComponent(selectedRef)}?tab=communications`}
                  >
                    پرونده ارتباطات
                  </Link>
                  {href ? (
                    <a className="sk-btn sk-btn--secondary sk-btn--sm" href={href}>
                      {props.channel === 'sms' ? 'بازکردن پیامک دستگاه' : 'شروع تماس دستگاه'}
                    </a>
                  ) : null}
                </div>
              </>
            ) : null}
          </section>
        </V2Card>
      </div>

      {selectedRef ? (
        <CustomerCommunicationsPanel
          base={base}
          partyRef={selectedRef}
          channel={props.channel}
          showAnalysis
        />
      ) : null}
    </div>
  );
}
